import { mapEase } from "./ease";
import { scheduleNext } from "./timing";

// Wiggle animation:
// Iterates through child drawable SVG elements and applies a small back-and-forth rotation.
// Children overlap in start times to create a lively staggered wiggle.
// loopBehavior: once | infinite | sporadic
export const wiggleAnim = (
	element,
	{
		gsap,
		loopBehavior = "infinite",
		animatedElements,
		sporadicTimers,
		duration = 1, // per child total (there and back via yoyo repeat:1)
		ease = "none",
		angle = 24, // degrees to rotate (0 -> +angle -> 0 -> -angle ... simplified to +angle then yoyo back)
		overlapRatio = 0.1,
		randomDelayMin = 3000,
		randomDelayMax = 8000,
	} = {},
) => {
	if (!element || !gsap || !animatedElements) return null;
	if (animatedElements.has(element)) return animatedElements.get(element);

	let observer;
	let tl = null;
	let isVisible = false;

	let targets = [];
	try {
		// Get all drawable SVG elements but exclude those inside mask elements
		targets = Array.from(
			element.querySelectorAll("path, circle, rect, polygon, line, polyline, ellipse, use"),
		).filter((el) => !el.closest("mask"));
	} catch (e) {}
	if (!targets.length) targets = [element];

	// Handle elastic easing specially later; keep original token for detection
	const adjustedEase = ease === "elastic" ? "elastic.inOut" : ease;
	const mappedEase = mapEase(adjustedEase);
	const perChildDuration = Math.max(0.05, duration);
	const offsetStep = perChildDuration * overlapRatio;
	const useElastic = adjustedEase.includes("elastic");
	const effectiveAngle = useElastic && Math.abs(angle) < 12 ? angle * 1.5 : angle;

	const buildTimeline = () => {
		const timeline = gsap.timeline({
			onComplete: () => {
				if (loopBehavior === "sporadic" && animatedElements.has(element) && isVisible) {
					animatedElements.set(element, null);
					scheduleNext({
						element,
						animatedElements,
						sporadicTimers,
						startSingleCycle: startSingleCycle,
						randomDelayMin,
						randomDelayMax,
					});
				}
			},
		});

		targets.forEach((t, i) => {
			if (!t.__wiggleBoxSet) {
				try {
					t.style.transformBox = "fill-box";
				} catch (e) {}
				try {
					const bb = t.getBBox();
					if (bb && (bb.width || bb.height)) {
						const cx = bb.x + bb.width / 2;
						const cy = bb.y + bb.height / 2;
						gsap.set(t, { svgOrigin: `${cx} ${cy}` });
					} else {
						gsap.set(t, { transformOrigin: "50% 50%" });
					}
				} catch (e) {
					gsap.set(t, { transformOrigin: "50% 50%" });
				}
				t.__wiggleBoxSet = true;
			}
			const startTime = i * offsetStep;
			if (useElastic) {
				const firstPortion = perChildDuration * 0.55;
				const secondPortion = perChildDuration - firstPortion;
				timeline
					.to(
						t,
						{
							rotation: effectiveAngle,
							ease: mappedEase,
							duration: firstPortion,
							transformOrigin: "50% 50%",
						},
						startTime,
					)
					.to(
						t,
						{ rotation: 0, ease: mappedEase, duration: secondPortion, transformOrigin: "50% 50%" },
						startTime + firstPortion,
					);
			} else {
				const seg = perChildDuration / 4;
				const mini = gsap.timeline();
				mini.to(t, { rotation: effectiveAngle, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" })
					.to(t, { rotation: -effectiveAngle, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" })
					.to(t, { rotation: effectiveAngle, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" })
					.to(t, { rotation: 0, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" });
				timeline.add(mini, startTime);
			}
		});
		return timeline;
	};

	const startSingleCycle = () => {
		if (isVisible) {
			tl = buildTimeline();
			animatedElements.set(element, tl);
		}
	};

	const startAnimation = () => {
		if (loopBehavior === "sporadic") {
			if (!animatedElements.has(element) || !animatedElements.get(element)) {
				startSingleCycle();
			} else {
				const currentTl = animatedElements.get(element);
				if (currentTl) currentTl.play();
			}
		} else {
			if (animatedElements.has(element) && animatedElements.get(element)) {
				animatedElements.get(element).play();
				return;
			}
			switch (loopBehavior) {
				case "once":
					tl = buildTimeline();
					break;
				case "infinite":
					tl = buildTimeline();
					tl.repeat(-1);
					break;
				default:
					tl = buildTimeline();
			}
			animatedElements.set(element, tl);
		}
	};

	const stopAnimation = () => {
		const currentTl = animatedElements.get(element);
		if (currentTl) {
			currentTl.pause();
		}
		if (loopBehavior === "sporadic" && sporadicTimers && sporadicTimers.has(element)) {
			clearTimeout(sporadicTimers.get(element));
			sporadicTimers.delete(element);
		}
	};

	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				isVisible = entry.isIntersecting;
				if (isVisible) {
					startAnimation();
				} else {
					stopAnimation();
				}
			});
		},
		{ threshold: 0.1 },
	);

	observer.observe(element);
	element._wiggleObserver = observer;

	return tl;
};

export const wigglePreview = (element, { gsap, duration = 1, ease = "ease", angle = 8, overlapRatio = 0.3 } = {}) => {
	if (!element || !gsap) return null;
	let targets = [];
	try {
		targets = Array.from(
			element.querySelectorAll("path, circle, rect, polygon, line, polyline, ellipse, use"),
		).filter((el) => !el.closest("mask"));
	} catch (e) {}
	if (!targets.length) targets = [element];

	const adjustedEase = ease === "elastic" ? "elastic.inOut" : ease;
	const mappedEase = mapEase(adjustedEase);
	const useElastic = adjustedEase.includes("elastic");
	const effectiveAngle = useElastic && Math.abs(angle) < 12 ? angle * 1.5 : angle;
	const perChildDuration = Math.max(0.05, duration);
	const offsetStep = perChildDuration * overlapRatio;
	const tl = gsap.timeline();
	targets.forEach((t, i) => {
		if (!t.__wiggleBoxSet) {
			try {
				t.style.transformBox = "fill-box";
			} catch (e) {}
			try {
				const bb = t.getBBox();
				if (bb && (bb.width || bb.height)) {
					const cx = bb.x + bb.width / 2;
					const cy = bb.y + bb.height / 2;
					gsap.set(t, { svgOrigin: `${cx} ${cy}` });
				} else {
					gsap.set(t, { transformOrigin: "50% 50%" });
				}
			} catch (e) {
				gsap.set(t, { transformOrigin: "50% 50%" });
			}
			t.__wiggleBoxSet = true;
		}
		const startTime = i * offsetStep;
		if (useElastic) {
			const firstPortion = perChildDuration * 0.55;
			const secondPortion = perChildDuration - firstPortion;
			tl.to(
				t,
				{ rotation: effectiveAngle, ease: mappedEase, duration: firstPortion, transformOrigin: "50% 50%" },
				startTime,
			).to(
				t,
				{ rotation: 0, ease: mappedEase, duration: secondPortion, transformOrigin: "50% 50%" },
				startTime + firstPortion,
			);
		} else {
			// Explicit 4-step wiggle: +angle -> -angle -> +angle -> 0 (equal segments)
			const seg = perChildDuration / 4;
			const mini = gsap.timeline();
			mini.to(t, { rotation: effectiveAngle, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" })
				.to(t, { rotation: -effectiveAngle, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" })
				.to(t, { rotation: effectiveAngle, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" })
				.to(t, { rotation: 0, duration: seg, ease: mappedEase, transformOrigin: "50% 50%" });
			tl.add(mini, startTime);
		}
	});
	return tl;
};
